import { useState } from "react";
import { loadStripe } from "@stripe/stripe-js/pure";
import { CardElement, useStripe, useElements, Elements } from "@stripe/react-stripe-js";
import { API } from "./calendarUtils";

// "/pure" = Stripe.js is NOT injected on import. advancedFraudSignals:false = no hidden
// m-outer iframe. That iframe's internal navigations add phantom history entries in
// Safari, which made the Back button appear dead. Stripe now loads only when a card
// form actually renders.
loadStripe.setLoadParameters({ advancedFraudSignals: false });
let stripePromise = null;
function getStripe() {
  if (!stripePromise) stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);
  return stripePromise;
}

const CARD_STYLE = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: { color: "#e25950" },
  },
  hidePostalCode: true,
};

// Inner component — must live inside Elements
function StripeCardFormInner({ amount, email, billingDetails, clientSecret, onSuccess, onError, submitLabel, disabled }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements || paying) return;
    setPaying(true);
    try {
      let secret = clientSecret;
      if (!secret) {
        const intentRes  = await fetch(`${API}/payments/intent`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, amount }),
        });
        const intentData = await intentRes.json();
        if (!intentRes.ok) throw new Error(intentData.error ?? "Payment setup failed.");
        secret = intentData.clientSecret;
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(secret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: billingDetails ?? { email },
        },
      });

      if (error) throw new Error(error.message);
      if (paymentIntent.status !== "succeeded") throw new Error("Payment did not complete. Please try again.");
      onSuccess(paymentIntent.id);
    } catch (err) {
      onError(err.message || "Something went wrong. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <>
      <div className="stripe-card-element">
        <CardElement options={CARD_STYLE} />
      </div>
      <button className="book-btn" style={{ marginTop: "1.25rem" }}
        disabled={disabled || paying} onClick={handleSubmit}>
        {paying ? "Processing…" : submitLabel}
      </button>
    </>
  );
}

// Public component — owns its own Elements context
export function StripeCardForm({ amount, email, billingDetails, clientSecret, onSuccess, onError, submitLabel = "Pay", disabled = false }) {
  return (
    <Elements stripe={getStripe()}>
      <StripeCardFormInner
        amount={amount}
        email={email}
        billingDetails={billingDetails}
        clientSecret={clientSecret}
        onSuccess={onSuccess}
        onError={onError}
        submitLabel={submitLabel}
        disabled={disabled}
      />
    </Elements>
  );
}
